/**
 * LA HOJA DE REVISIÓN (CQ4a · D23): lo que el titular mira ANTES de firmar.
 *
 * Cada celda del cuaderno con su ORIGEN —dato (está en Pest tal cual), cálculo
 * (lo calcula el código: dosis/ha, superficie, nº de orden), defecto (goteo,
 * invernadero, «NO» en 3.2–3.5, «Buena»), usuario (lo contestó una persona),
 * deducido (máquina o aplicador que el parte no guardó, CU2: «confírmalo»)— y
 * cada HUECO con su motivo. Más las líneas que el borrador saca del cuaderno con
 * su motivo, y las que se pidió sacar y no se pudo (fitosanitarios con nº, D16).
 *
 * NO SE IMPRIME en la copia de la Junta: es una hoja interna. Va en español como
 * el cuaderno (D15); el cromo de la pantalla que la enseña va por i18n.
 *
 * PURO: sin red.
 */
import type { EntradaCuaderno } from "./entrada";
import { SECCIONES, type Hueco, type MotivoHueco, type Origen, type Seccion } from "./modeloOficial";
import type { CuadernoDataInput } from "./types";
import { dosis as fmtDosis, fecha as fmtFecha, intervalo, num, parcelas as fmtParcelas, producto as fmtProducto } from "./format";

export type OrigenRevision = Origen | "hueco";

export interface CeldaRevision {
  seccion: Seccion;
  /** Fila de su apartado (0, 1…), o null si el apartado no es una tabla. */
  fila: number | null;
  /** Campo del modelo («dosis», «producto.numeroRegistro», «titular.nif»…). */
  campo: string;
  /** Cómo lo llama el modelo de la Junta. */
  etiqueta: string;
  /** De qué fila se habla: «14/01/2026 · VERTIMEC», «Parcela 3», el nombre de la persona… */
  contexto: string | null;
  /** El valor tal y como se imprime («» si es un hueco). */
  valor: string;
  origen: OrigenRevision;
  motivo?: MotivoHueco;
}

export interface LineaRevision {
  linea: string;
  producto: string;
  fecha: string | null;
  motivo: string;
}

/** CU9: una fila del 3.1 que la persona cambió en el documento o añadió (los partes no cambian). */
export interface FilaDocumento {
  fila: number;
  producto: string;
  fecha: string | null;
  /** Los campos que decidió la persona (dosis, producto, fecha…). */
  campos: string[];
  /** La fila no viene de ningún parte. */
  anadida: boolean;
}

export interface HojaRevision {
  celdas: CeldaRevision[];
  /** Líneas que el borrador saca del cuaderno, con su motivo (desde CU9, también fitosanitarios). */
  excluidas: LineaRevision[];
  /** Líneas que se pidió sacar y NO salen. Desde CU9 (28-09-2026) siempre vacía. */
  noSePuedenQuitar: LineaRevision[];
  /** CU9: filas del 3.1 cambiadas o añadidas por la persona en el documento. */
  documento: FilaDocumento[];
  resumen: Record<OrigenRevision, number>;
}

/** Nombres de los campos como los escribe el modelo v9. */
const ETIQUETAS: Record<string, string> = {
  fechaApertura: "Fecha de apertura del cuaderno",
  razonSocial: "Nombre y apellidos o razón social",
  nif: "NIF",
  reaNacional: "Nº de Registro de Explotaciones (nacional)",
  reaAutonomico: "Nº de Registro de Explotaciones (autonómico)",
  direccion: "Dirección",
  localidad: "Localidad",
  codigoPostal: "C. Postal",
  provincia: "Provincia",
  telefonoFijo: "Teléfono fijo",
  telefonoMovil: "Teléfono móvil",
  email: "e-mail",
  "titular.nombre": "Titular o representante: nombre",
  "titular.nif": "Titular o representante: NIF",
  "titular.direccion": "Titular o representante: dirección",
  "titular.localidad": "Titular o representante: localidad",
  "titular.codigoPostal": "Titular o representante: C. Postal",
  "titular.provincia": "Titular o representante: provincia",
  "titular.tipoRepresentacion": "Tipo de representación",
  "titular.telefono": "Titular o representante: teléfono",
  "titular.email": "Titular o representante: e-mail",
  nombre: "Nombre y apellidos",
  descripcion: "Descripción del equipo",
  ropo: "Nº de inscripción ROPO / nº de carné",
  carne: "Tipo de carné",
  roma: "Nº de inscripción en el ROMA",
  fechaAdquisicion: "Fecha de adquisición",
  identificacion: "Nº ROPO del asesor",
  orden: "Nº de orden",
  sigpac: "Referencia SIGPAC",
  superficieCultivadaHa: "Superficie cultivada (ha)",
  especie: "Especie",
  variedad: "Variedad",
  secanoRegadio: "Secano / regadío",
  aireLibreProtegido: "Aire libre / protegido",
  sistemaGip: "Sistema de asesoramiento (GIP)",
  fechas: "Fecha",
  parcelas: "Id. parcelas",
  superficieHa: "Superficie tratada (ha)",
  problema: "Problema fitosanitario",
  aplicador: "Aplicador (nº de orden)",
  equipo: "Equipo (nº de orden)",
  producto: "Nombre comercial",
  "producto.numeroRegistro": "Nº de registro",
  numeroRegistro: "Nº de registro",
  "producto.sustanciaActiva": "Sustancia activa",
  dosis: "Dosis",
  eficacia: "Eficacia",
  observaciones: "Observaciones",
  aplica: "Aplica tratamiento (SI/NO)",
};

const SECCIONES_POR_LONGITUD = [...SECCIONES].sort((a, b) => b.length - a.length);

/** «3.1.4.dosis» → { seccion: "3.1", fila: 4, campo: "dosis" }; «2.2.0» → campo «». */
export function partirClave(clave: string): { seccion: Seccion; fila: number | null; campo: string } | null {
  const seccion = SECCIONES_POR_LONGITUD.find((s) => clave === s || clave.startsWith(`${s}.`));
  if (!seccion) return null;
  const resto = clave.slice(seccion.length + 1);
  const m = /^(\d+)(?:\.(.*))?$/.exec(resto);
  if (m) return { seccion, fila: Number(m[1]), campo: m[2] ?? "" };
  return { seccion, fila: null, campo: resto };
}

function leer(obj: unknown, ruta: string[]): unknown {
  let cur = obj;
  for (const k of ruta) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[k];
  }
  return cur;
}

/** El valor como se imprime en el cuaderno (español, D15). */
export function valorTexto(v: unknown): string {
  if (v == null || v === "") return "";
  if (typeof v === "boolean") return v ? "SI" : "NO";
  if (typeof v === "number") return num(v, 4);
  if (typeof v === "string") return /^\d{4}-\d{2}-\d{2}$/.test(v) ? fmtFecha(v) : v;
  if (Array.isArray(v)) return v.every((x) => typeof x === "number") ? fmtParcelas(v as number[]) : v.map(valorTexto).join(", ");
  const o = v as Record<string, unknown>;
  if ("valor" in o && "unidad" in o) return fmtDosis(o as { valor?: number; unidad?: string });
  if ("desde" in o) return intervalo(o as { desde?: string; hasta?: string });
  if ("nombreComercial" in o) return fmtProducto(o as { nombreComercial: string });
  if (["provincia", "poligono", "parcela", "recinto"].some((k) => k in o)) {
    return ["provincia", "terminoMunicipal", "agregado", "zona", "poligono", "parcela", "recinto"]
      .map((k) => (o[k] == null ? "" : String(o[k])))
      .filter(Boolean)
      .join("-");
  }
  return Object.entries(o)
    .filter(([, x]) => x != null && x !== "" && typeof x !== "object")
    .map(([k, x]) => `${k}: ${valorTexto(x)}`)
    .join(" · ");
}

function contextoDe(data: CuadernoDataInput, seccion: Seccion, fila: number | null): string | null {
  if (fila == null) return null;
  const tabla = (data as Record<string, unknown>)[seccion];
  const row = Array.isArray(tabla) ? (tabla[fila] as Record<string, unknown> | undefined) : undefined;
  if (!row) return null;
  if (seccion === "3.1") {
    const f = row.fechas as { desde?: string; hasta?: string } | undefined;
    const p = row.producto as { nombreComercial?: string } | undefined;
    return [intervalo(f), p?.nombreComercial].filter(Boolean).join(" · ") || null;
  }
  if (seccion === "1.2") return (row.nombre as string) ?? null;
  if (seccion === "1.3") return (row.descripcion as string) ?? null;
  if (seccion === "2.1") return row.orden != null ? `Parcela ${row.orden}` : null;
  if (seccion === "2.2") return `Parcelas ${fmtParcelas(row.parcelas as number[] | "TODAS")}`;
  return null;
}

export interface EntradaRevision {
  data: CuadernoDataInput;
  origenes: Record<string, Origen>;
  huecos: Hueco[];
  entrada: Pick<EntradaCuaderno, "lineas" | "partes">;
  excluidas: Record<string, string>;
  noSePuedenQuitar: string[];
  /** Meta del 3.1 (CU9: qué decidió la persona en cada fila). */
  meta31?: { usuario?: string[]; anadida?: boolean }[];
}

export function hojaDeRevision(r: EntradaRevision): HojaRevision {
  const celdas: CeldaRevision[] = [];
  const vistas = new Set<string>();
  const etiqueta = (campo: string) => ETIQUETAS[campo] ?? ETIQUETAS[campo.split(".").pop() ?? ""] ?? campo;

  for (const [clave, origen] of Object.entries(r.origenes)) {
    const p = partirClave(clave);
    if (!p) continue;
    const base = (r.data as Record<string, unknown>)[p.seccion];
    const ruta = [...(p.fila != null ? [String(p.fila)] : []), ...(p.campo ? p.campo.split(".") : [])];
    const valor = valorTexto(leer(base, ruta));
    vistas.add(`${p.seccion}|${p.fila ?? ""}|${p.campo}`);
    celdas.push({
      seccion: p.seccion,
      fila: p.fila,
      campo: p.campo,
      etiqueta: p.campo ? etiqueta(p.campo) : "Fila",
      contexto: contextoDe(r.data, p.seccion, p.fila),
      valor,
      origen,
    });
  }

  for (const h of r.huecos) {
    const campo = h.seccion === "3.1" && h.campo === "numeroRegistro" ? "producto.numeroRegistro" : h.campo;
    const k = `${h.seccion}|${h.fila ?? ""}|${campo}`;
    if (vistas.has(k)) {
      // Un valor que se enseña pero además cuenta como hueco (p. ej. SIGPAC sin confirmar).
      const c = celdas.find((x) => `${x.seccion}|${x.fila ?? ""}|${x.campo}` === k);
      if (c) {
        c.origen = "hueco";
        c.motivo = h.motivo;
      }
      continue;
    }
    vistas.add(k);
    celdas.push({
      seccion: h.seccion,
      fila: h.fila ?? null,
      campo,
      etiqueta: etiqueta(campo),
      contexto: contextoDe(r.data, h.seccion, h.fila ?? null),
      valor: "",
      origen: "hueco",
      motivo: h.motivo,
    });
  }

  const orden = new Map(SECCIONES.map((s, i) => [s, i]));
  celdas.sort(
    (a, b) =>
      (orden.get(a.seccion) ?? 99) - (orden.get(b.seccion) ?? 99) ||
      (a.fila ?? -1) - (b.fila ?? -1) ||
      a.campo.localeCompare(b.campo),
  );

  const partes = new Map(r.entrada.partes.map((p) => [p.id, p]));
  const lineas = new Map(r.entrada.lineas.map((l) => [l.id, l]));
  const describir = (id: string, motivo: string): LineaRevision => {
    const l = lineas.get(id);
    const p = l ? partes.get(l.parteId) : undefined;
    return { linea: id, producto: l?.nombre?.trim() || "—", fecha: p?.fecha ?? null, motivo };
  };

  const noSe = new Set(r.noSePuedenQuitar);
  const excluidas = Object.entries(r.excluidas)
    .filter(([id]) => !noSe.has(id) && lineas.has(id))
    .map(([id, motivo]) => describir(id, motivo));
  const noSePuedenQuitar = r.noSePuedenQuitar.map((id) => describir(id, r.excluidas[id] ?? "—"));

  const resumen: Record<OrigenRevision, number> = { dato: 0, calculo: 0, defecto: 0, usuario: 0, deducido: 0, hueco: 0 };
  for (const c of celdas) resumen[c.origen] += 1;

  const filas31 = r.data["3.1"] ?? [];
  const documento: FilaDocumento[] = [];
  (r.meta31 ?? []).forEach((m, i) => {
    if (!m?.usuario?.length && !m?.anadida) return;
    const f = filas31[i];
    documento.push({
      fila: i,
      producto: f?.producto?.nombreComercial ?? "—",
      fecha: f?.fechas?.desde ?? null,
      campos: [...(m.usuario ?? [])],
      anadida: !!m.anadida,
    });
  });

  return { celdas, excluidas, noSePuedenQuitar, documento, resumen };
}
